import { getPerceptualColorDistance } from '@/utils/colorMatch.utils';
import { mixComponentsToRgb, rgbToHsl } from '@/utils/gameMixColor.utils';

export const BASE_COLORS = {
  red: { labelKey: 'colors.red', rgb: { r: 255, g: 0, b: 0 } },
  yellow: { labelKey: 'colors.yellow', rgb: { r: 255, g: 255, b: 0 } },
  blue: { labelKey: 'colors.blue', rgb: { r: 0, g: 0, b: 255 } },
  white: { labelKey: 'colors.white', rgb: { r: 255, g: 255, b: 255 } },
  black: { labelKey: 'colors.black', rgb: { r: 0, g: 0, b: 0 } },
};

export const COLOR_KEYS = Object.keys(BASE_COLORS);
export const EMPTY_COLOR = { r: 236, g: 236, b: 240 };
export const TARGET_PROFILE_COUNT = 4;
export const MIN_CONSECUTIVE_DISTANCE = 15;

const PRIMARY_KEYS = ['red', 'yellow', 'blue'];
const MAX_ATTEMPTS = 80;

// Rotate through vivid, soft, deep, and muted targets across rounds.
const PROFILE_RANGES = [
  {
    primary: [3, 9], secondary: [0, 4], third: [0, 1], white: [0, 2], black: [0, 1],
    accepts: (s, l) => s >= 0.55 && l >= 0.25 && l <= 0.75,
  },
  {
    primary: [1, 4], secondary: [0, 2], third: [0, 1], white: [8, 15], black: [0, 0],
    accepts: (s, l) => s >= 0.18 && s <= 0.65 && l >= 0.48 && l <= 0.82,
  },
  {
    primary: [2, 7], secondary: [0, 4], third: [0, 2], white: [0, 2], black: [1, 5],
    accepts: (s, l) => s >= 0.18 && l >= 0.15 && l <= 0.45,
  },
  {
    primary: [1, 5], secondary: [1, 5], third: [0, 3], white: [3, 8], black: [0, 2],
    accepts: (s, l) => s >= 0.08 && s <= 0.45 && l >= 0.25 && l <= 0.7,
  },
];

const FALLBACK_MIXES = [
  { red: 5, yellow: 0, blue: 0, white: 2, black: 0 },
  { red: 0, yellow: 5, blue: 0, white: 2, black: 0 },
  { red: 0, yellow: 0, blue: 5, white: 2, black: 0 },
  { red: 3, yellow: 4, blue: 0, white: 0, black: 0 },
  { red: 0, yellow: 3, blue: 4, white: 0, black: 0 },
  { red: 3, yellow: 0, blue: 4, white: 0, black: 0 },
];

const randomInt = ([min, max], random) => min + Math.floor(random() * (max - min + 1));

const createProfileMix = (profile, random) => {
  const dominantIndex = randomInt([0, 2], random);
  const secondaryIndex = (dominantIndex + 1 + randomInt([0, 1], random)) % 3;
  const thirdIndex = 3 - dominantIndex - secondaryIndex;

  return {
    [PRIMARY_KEYS[dominantIndex]]: randomInt(profile.primary, random),
    [PRIMARY_KEYS[secondaryIndex]]: randomInt(profile.secondary, random),
    [PRIMARY_KEYS[thirdIndex]]: randomInt(profile.third, random),
    white: randomInt(profile.white, random),
    black: randomInt(profile.black, random),
  };
};

export const generateTargetColor = ({ round, previousColor = null, profileOffset = 0, random = Math.random }) => {
  const profileIndex = ((Math.max(1, Math.floor(round)) - 1 + profileOffset) % TARGET_PROFILE_COUNT + TARGET_PROFILE_COUNT) % TARGET_PROFILE_COUNT;
  const profile = PROFILE_RANGES[profileIndex];

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const mix = createProfileMix(profile, random);
    const color = mixComponentsToRgb(mix, BASE_COLORS, EMPTY_COLOR);
    const [, saturation, lightness] = rgbToHsl(color);

    if (profile.accepts(saturation, lightness)
      && (!previousColor || getPerceptualColorDistance(color, previousColor) >= MIN_CONSECUTIVE_DISTANCE)) {
      return color;
    }
  }

  const fallbackColors = FALLBACK_MIXES.map((mix) => mixComponentsToRgb(mix, BASE_COLORS, EMPTY_COLOR));
  return fallbackColors.reduce((best, color) => (
    !previousColor || getPerceptualColorDistance(color, previousColor) > getPerceptualColorDistance(best, previousColor) ? color : best
  ));
};
